// Basic
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cn } from '../../../../../../libraries/tailwind/utils';
// Components
import Entity from '../../../../entity/Entity';
import ICONS from '../../../../../../enums/icons';
import TooltipHelper from '@/common-utils/components/renders/tooltip/TooltipHelper';
import { Form, FormControl, FormField, FormItem } from '../../../../../ui/form';
import { Input } from '../../../../../ui/input';
import { ModalBody } from '../../../../../ui/modal';
import { Button } from '@/common-utils/components/ui/button';
// Common
import Icon from '@/common-utils/components/renders/icon/Icon';
import genUID from '@/common-utils/functions/generateUniqueId';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsUser } from '@/app/i18n/namespaces';
// Style
import './BodySetMails.scss';

/**
 * @param root0
 * @param root0.step
 * @param root0.message
 * @param root0.confirm
 * @param root0.renderList
 * @param root0.setRenderList
 * @param root0.disableAction
 * @param root0.setDisableAction
 * @param root0.maxEmailsNumber
 * @param root0.existingEmails
 * @param root0.linkValidity
 * @returns {JSX} BodySetMails
 */
function BodySetMails({
    step,
    message,
    confirm,
    renderList,
    setRenderList,
    linkValidity = null,
    disableAction,
    setDisableAction,
    maxEmailsNumber,
    existingEmails = []
}) {
    // Constants
    const STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.send_mail.steps';

    // State
    const [mailEntryError, setMailEntryError] = useState('');

    // Form
    const formSchema = z.object({
        email: z.string().email()
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: { email: '' }
    });

    // Hooks
    const { stepTitle } = useStepConfig(
        step,
        () => ({
            0: { stepTitle: `${STEP_TITLE_PREFIX}.input` },
            1: { stepTitle: `${STEP_TITLE_PREFIX}.confirm` }
        }),
        []
    );

    useEffect(() => {
        setDisableAction(renderList.some(el => !el.isValid || el.alreadyExist || !el.email));
    }, [renderList]);


    // Functions
    // Validate the whole mails list
    const handleMailsListValidation = (mailsList) => {
        const newList = mailsList.map((item, index) => {
            const newItem = { ...item };
            const restList = mailsList.filter((_, idx) => idx !== index);

            newItem.isValid = z.string().email().safeParse(newItem.email).success;
            newItem.alreadyExist = restList.some((el) => el.email === item.email) || existingEmails.includes(newItem.email);
            return newItem;
        });

        // Update state
        setMailEntryError(newList.some((el) => el.alreadyExist) ? 'modal.send_mail.already_exists' : '');
        setRenderList(newList);
    };

    // Validate single mail input
    const handleInputChange = (index, field, value) => {
        const newList = renderList.map((item, idx) => {
            const newItem = { ...item, [field]: value };
            if (idx === index) {
                if (field === 'email') {
                    newItem.isValid = z.string().email().safeParse(value).success;
                    newItem.alreadyExist = renderList.some((el, i) => el.email === value && i !== index) || existingEmails.includes(newItem.email);
                }
                return newItem;
            }
            return item;
        });

        handleMailsListValidation(newList);
    };

    const handleAddMail = (index) => {
        const isEmailValid = renderList[index]?.isValid;
        const alreadyExist = renderList[index]?.alreadyExist;

        if (isEmailValid && !alreadyExist) {
            setRenderList([...renderList, { id: genUID(), email: '', isValid: false }]);
        }
    };

    const handleRemoveMail = (e, index) => {
        e.preventDefault();

        const newList = renderList.filter((_, idx) => idx !== index);
        handleMailsListValidation(newList);
    };

    // Render
    return (
        <ModalBody className='body-send-mail'>
            <Entity
                entity={stepTitle}
                data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                className={'mb-4 flex'}
            />
            {(step === 0 || (step === 1 && linkValidity)) &&
                (
                    <div className='send-mail-collection space-y-2'>
                        <Entity
                            className='font-semibold'
                            entity={message.entity}
                            namespace={message.namespace}
                            data={message.data}
                        />
                        {linkValidity &&
                            <div className='flex justify-center items-center'>
                                <Icon icon={ICONS.WARNING} className={cn('text-warning mr-1')} />
                                <Entity
                                    className='font-semibold'
                                    entity={linkValidity.entity}
                                    namespace={linkValidity.namespace}
                                />
                            </div>
                        }
                    </div>
                )
            }
            {step === 0 &&
                <div className='modal-add-email'>
                    <div className='addresses-list mt-4'>
                        <Form {...form}>
                            <form>
                                {renderList.map((mail, index) => (
                                    <div className='addresses-form-line' key={mail.id}>
                                        <FormField
                                            name={`email-${index}`}
                                            control={form.control}
                                            render={({ field }) => (
                                                <FormItem className='addresses-list-input'>
                                                    <FormControl>
                                                        <Input
                                                            {...field}
                                                            className={cn(mail.alreadyExist && 'border-red-500')}
                                                            autoFocus
                                                            type='email'
                                                            entity='modal.send_mail.placeholder'
                                                            namespace={nsUser}
                                                            autoComplete='off'
                                                            value={mail.email}
                                                            onChange={(e) => {
                                                                field.onChange(e);
                                                                handleInputChange(index, 'email', e.target.value);
                                                            }}
                                                        />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                        {renderList.length - 1 === index &&
                                            renderList.length < maxEmailsNumber ? (
                                            <TooltipHelper
                                                asChild
                                                trigger={
                                                    <Button
                                                        onClick={() => handleAddMail(index)}
                                                        icon={ICONS.ADD}
                                                        variant='success-outline'
                                                        className={cn('button-add ml-2 h-8 w-8 rounded-full border')}
                                                        disabled={disableAction}
                                                    />
                                                }
                                                content={
                                                    <Entity
                                                        entity='modal.send_mail.add_address'
                                                        namespace={nsUser}
                                                    />
                                                }
                                            />

                                        ) : null}
                                        {index > 0 && (
                                            <TooltipHelper
                                                asChild
                                                trigger={
                                                    <Button
                                                        onClick={(e) => handleRemoveMail(e, index)}
                                                        icon={ICONS.REMOVE}
                                                        variant='danger-outline'
                                                        className={cn('button-remove ml-2 h-8 w-8 rounded-full border')}
                                                    />
                                                }
                                                content={
                                                    <Entity
                                                        entity='modal.send_mail.remove_address'
                                                        namespace={nsUser}
                                                    />
                                                }
                                            />
                                        )}
                                    </div>
                                ))}
                            </form>
                        </Form>
                    </div>
                    <Entity
                        className='block text-center italic font-semibold'
                        entity='modal.send_mail.maximum_mails'
                        data={{ number: maxEmailsNumber }}
                    />
                    <Entity
                        className='block mt-1 font-semibold text-danger text-center'
                        entity={mailEntryError}
                        namespace={nsUser}
                    />
                </div >
            }
            {step === 1 &&
                <div className='text-center mt-4'>
                    <Entity
                        className='font-semibold'
                        entity={confirm.entity}
                        namespace={confirm.namespace}
                    />
                    <ul className='mt-2 mb-2 font-semibold'>
                        {renderList.map((item) => (
                            <li key={item.id}>{item.email}</li>
                        ))}
                    </ul>
                    <Entity
                        className='font-semibold'
                        entity='modal.send_mail.validate'
                        namespace={nsUser}
                    />
                </div>
            }
        </ModalBody>
    );
}

BodySetMails.propTypes = {
    step: PropTypes.number.isRequired,
    message: PropTypes.object,
    confirm: PropTypes.object,
    renderList: PropTypes.array,
    setRenderList: PropTypes.func,
    disableAction: PropTypes.bool,
    setDisableAction: PropTypes.func,
    maxEmailsNumber: PropTypes.number,
    existingEmails: PropTypes.array,
    linkValidity: PropTypes.object
};

export default BodySetMails;
